import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { transactionAPI } from '../services/api';
import { ArrowLeft, Save, User, MapPin, DollarSign, FileText, FileCheck, Building, Plus, X, CheckCircle, File } from 'lucide-react';
import { formatDateForInput, formatSaveErrorMessage } from '../utils/formatters';
import FileUpload from '../components/FileUpload';
import NumberInput from '../components/NumberInput';
import { useToast } from '../contexts/ToastContext';

// 숫자 포맷팅 유틸리티 함수들
const formatNumber = (value) => {
  if (value === null || value === undefined || value === 0 || value === '') return '0';
  // 소수점 이하를 제거하고 정수 부분만 추출
  const integerPart = value.toString().split('.')[0];
  // 숫자가 아닌 문자 제거
  const numericValue = integerPart.replace(/[^\d]/g, '');
  // 빈 문자열이면 0 반환
  if (numericValue === '') return '0';
  // 세자릿수마다 쉼표 추가
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const parseNumber = (value) => {
  if (!value) return 0;
  // 쉼표 제거하고 숫자만 추출
  const numericValue = value.toString().replace(/[^\d]/g, '');
  return parseInt(numericValue, 10) || 0;
};

// 서류 타입 상수
const DOCUMENT_TYPES = {
  DEATH_CERTIFICATE: 'death_certificate',
  BURIAL_CERTIFICATE: 'burial_certificate',
  FAMILY_RELATION_CERTIFICATE: 'family_relation_certificate',
  FAMILY_REGISTER: 'family_register',
  CREMATION_CERTIFICATE: 'cremation_certificate',
  PRIVACY_CONSENT: 'privacy_consent',
  OTHER: 'other'
};

const DOCUMENT_TYPE_LABELS = {
  [DOCUMENT_TYPES.DEATH_CERTIFICATE]: '사망진단서',
          [DOCUMENT_TYPES.BURIAL_CERTIFICATE]: '매장신고서',
  [DOCUMENT_TYPES.FAMILY_RELATION_CERTIFICATE]: '가족관계증명서',
  [DOCUMENT_TYPES.FAMILY_REGISTER]: '제적등본',
  [DOCUMENT_TYPES.CREMATION_CERTIFICATE]: '화장증명서',
  [DOCUMENT_TYPES.PRIVACY_CONSENT]: '개인정보동의서',
  [DOCUMENT_TYPES.OTHER]: '기타'
};

const ContractForm = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    contract_date: new Date().toISOString().split('T')[0],
    type: '신규',
    status: '진행중',
    memo: '',
    dead: [{
      name: '',
      gender: '',
      burial_method: '',
      burial_method_1: '',
      burial_method_2: '',
      tomb_type: '',
      death_date: '',
      report_date: ''
    }],
    mausoleum: {
      member_number: '',
      complex: '',
      area: '',
      row_number: '',
      number: '',
      area_size: '',
      period: '',
      cemetery_id: 1,
      etc: ''
    },
    acquaintances: [{
      name: '',
      relationship: '',
      phone: '',
      address: ''
    }],
    cost: {
      usage_fee: 0,
      management_fee: 0,
      burial_work_fee: 0,
      installation_fee: 0,
      repair_fee: 0,
      removal_work_fee: 0,
      other_work_fee: 0,
      stone_processing_fee: 0,
      agency_fee: 0,
      total_cost: 0,
      memo: ''
    },
    documents: [],
    stone_object: {
      tomb_type: '',
      tomb_table: '',
      memorial_stone: '',
      gravestone: '',
      vases: [{
        size: ''
      }]
    }
  });

  // 기존 계약 데이터 불러오기 (수정 모드일 때)
  const { data: existingContract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => transactionAPI.getById(id),
    select: (response) => response.data,
    enabled: isEditMode,
  });

  const createContractMutation = useMutation({
    mutationFn: (data) => transactionAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contracts']);
      success('계약이 성공적으로 생성되었습니다.');
      navigate('/contracts');
    },
    onError: (error) => {
      console.error('계약 생성 오류:', error);
      showError(formatSaveErrorMessage(error, '계약 생성에 실패했습니다.'));
    }
  });

  const updateContractMutation = useMutation({
    mutationFn: (data) => transactionAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['contracts']);
      queryClient.invalidateQueries(['contract', id]);
      success('계약이 성공적으로 수정되었습니다.');
      navigate(`/contracts/${id}`);
    },
    onError: (error) => {
      console.error('계약 수정 오류:', error);
      showError(formatSaveErrorMessage(error, '계약 수정에 실패했습니다.'));
    }
  });

  const handleInputChange = (section, field, value, index = 0) => {
    if (section === 'dead' || section === 'acquaintances') {
      // 배열 형태의 데이터 처리
      setFormData(prev => ({
        ...prev,
        [section]: prev[section].map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      }));
    } else if (section === 'vases') {
      // 화병 배열 데이터 처리
      setFormData(prev => ({
        ...prev,
        stone_object: {
          ...prev.stone_object,
          vases: prev.stone_object.vases.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
          )
        }
      }));
    } else if (section === 'documents') {
      // 서류 추가/수정 처리
      handleDocumentChange(field, value);
    } else if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // 서류 추가/수정 처리
  const handleDocumentChange = (documentType, fileUrl, fileName = '') => {
    setFormData(prev => {
      const existingDocuments = prev.documents || [];
      const existingIndex = existingDocuments.findIndex(doc => doc.document_type === documentType);

      if (existingIndex >= 0) {
        // 기존 서류가 있으면 추가
        const updatedDocuments = [...existingDocuments];
        updatedDocuments.push({
          document_type: documentType,
          file_link: fileUrl,
          file_name: fileName || getFileNameFromUrl(fileUrl)
        });
        return { ...prev, documents: updatedDocuments };
      } else {
        // 새로운 서류 추가
        return {
          ...prev,
          documents: [...existingDocuments, {
            document_type: documentType,
            file_link: fileUrl,
            file_name: fileName || getFileNameFromUrl(fileUrl)
          }]
        };
      }
    });
  };

  // 서류 삭제
  const removeDocument = (documentType, fileLink) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter(doc =>
        !(doc.document_type === documentType && doc.file_link === fileLink)
      )
    }));
  };

  // URL에서 파일명 추출
  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      return decodeURIComponent(pathParts[pathParts.length - 1]);
    } catch {
      return url;
    }
  };

  // 사망자 추가
  const addDead = () => {
    setFormData(prev => ({
      ...prev,
      dead: [...prev.dead, {
        name: '',
        gender: '',
        burial_method: '',
        tomb_type: '',
        death_date: '',
        report_date: ''
      }]
    }));
  };

  // 사망자 제거
  const removeDead = (index) => {
    if (formData.dead.length > 1) {
      setFormData(prev => ({
        ...prev,
        dead: prev.dead.filter((_, i) => i !== index)
      }));
    }
  };

  // 연고자 추가
  const addAcquaintance = () => {
    setFormData(prev => ({
      ...prev,
      acquaintances: [...prev.acquaintances, {
        name: '',
        relationship: '',
        phone: '',
        address: ''
      }]
    }));
  };

  // 연고자 제거
  const removeAcquaintance = (index) => {
    if (formData.acquaintances.length > 1) {
      setFormData(prev => ({
        ...prev,
        acquaintances: prev.acquaintances.filter((_, i) => i !== index)
      }));
    }
  };

  // 화병 추가
  const addVase = () => {
    setFormData(prev => ({
      ...prev,
      stone_object: {
        ...prev.stone_object,
        vases: [...prev.stone_object.vases, { size: '' }]
      }
    }));
  };

  // 화병 제거
  const removeVase = (index) => {
    if (formData.stone_object.vases.length > 1) {
      setFormData(prev => ({
        ...prev,
        stone_object: {
          ...prev.stone_object,
          vases: prev.stone_object.vases.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const calculateTotalCost = () => {
    const {
      usage_fee, management_fee, burial_work_fee, installation_fee,
      repair_fee, removal_work_fee, other_work_fee, stone_processing_fee, agency_fee
    } = formData.cost;

    const total = (
      (parseInt(usage_fee) || 0) +
      (parseInt(management_fee) || 0) +
      (parseInt(burial_work_fee) || 0) +
      (parseInt(installation_fee) || 0) +
      (parseInt(repair_fee) || 0) +
      (parseInt(removal_work_fee) || 0) +
      (parseInt(other_work_fee) || 0) +
      (parseInt(stone_processing_fee) || 0) +
      (parseInt(agency_fee) || 0)
    );

    setFormData(prev => ({
      ...prev,
      cost: {
        ...prev.cost,
        total_cost: total
      }
    }));
  };



  // 기존 데이터를 폼에 로드
  useEffect(() => {
    if (existingContract && isEditMode) {
      console.log('기존 계약 데이터:', existingContract); // 디버깅용

      // 기존 사망자 데이터 처리 (deads 배열)
      const deadArray = existingContract.deads?.map(dead => ({
        name: dead.name || '',
        gender: dead.gender || '',
        burial_method: dead.burial_method || '',
        burial_method_1: dead.burial_method_1 || '',
        burial_method_2: dead.burial_method_2 || '',
        tomb_type: dead.tomb_type || '',
        death_date: formatDateForInput(dead.death_date) || '',
        report_date: formatDateForInput(dead.report_date) || ''
      })) || [{
        name: '',
        gender: '',
        burial_method: '',
        burial_method_1: '',
        burial_method_2: '',
        tomb_type: '',
        death_date: '',
        report_date: ''
      }];

      // 기존 연고자 데이터 처리 (각 사망자의 acquaintance1, acquaintance2)
      const acquaintancesSet = new Set(); // 중복 제거를 위한 Set
      const acquaintancesArray = [];

      existingContract.deads?.forEach(dead => {
        if (dead.acquaintance1) {
          const key = `${dead.acquaintance1.id}-${dead.acquaintance1.name}`;
          if (!acquaintancesSet.has(key)) {
            acquaintancesSet.add(key);
            acquaintancesArray.push({
              name: dead.acquaintance1.name || '',
              relationship: dead.acquaintance1.relationship || '',
              phone: dead.acquaintance1.phone || '',
              address: dead.acquaintance1.address || ''
            });
          }
        }
        if (dead.acquaintance2) {
          const key = `${dead.acquaintance2.id}-${dead.acquaintance2.name}`;
          if (!acquaintancesSet.has(key)) {
            acquaintancesSet.add(key);
            acquaintancesArray.push({
              name: dead.acquaintance2.name || '',
              relationship: dead.acquaintance2.relationship || '',
              phone: dead.acquaintance2.phone || '',
              address: dead.acquaintance2.address || ''
            });
          }
        }
      });

      // 연고자가 없으면 기본값 설정
      if (acquaintancesArray.length === 0) {
        acquaintancesArray.push({
          name: '',
          relationship: '',
          phone: '',
          address: ''
        });
      }

      setFormData({
        contract_date: formatDateForInput(existingContract.contract_date) || new Date().toISOString().split('T')[0],
        type: existingContract.type || '신규',
        status: existingContract.status || '진행중',
        memo: existingContract.memo || '',
        dead: deadArray,
        mausoleum: {
          member_number: existingContract.mausoleum?.member_number || '',
          complex: existingContract.mausoleum?.complex || '',
          area: existingContract.mausoleum?.area || '',
          row_number: existingContract.mausoleum?.row_number || '',
          number: existingContract.mausoleum?.number || '',
          area_size: existingContract.mausoleum?.area_size || '',
          period: existingContract.mausoleum?.period || '',
          cemetery_id: existingContract.mausoleum?.cemetery_id || 1,
          etc: existingContract.mausoleum?.etc || ''
        },
        acquaintances: acquaintancesArray,
        cost: {
          usage_fee: existingContract.cost?.usage_fee || 0,
          management_fee: existingContract.cost?.management_fee || 0,
          burial_work_fee: existingContract.cost?.burial_work_fee || 0,
          installation_fee: existingContract.cost?.installation_fee || 0,
          repair_fee: existingContract.cost?.repair_fee || 0,
          removal_work_fee: existingContract.cost?.removal_work_fee || 0,
          other_work_fee: existingContract.cost?.other_work_fee || 0,
          stone_processing_fee: existingContract.cost?.stone_processing_fee || 0,
          agency_fee: existingContract.cost?.agency_fee || 0,
          total_cost: existingContract.cost?.total_cost || 0,
          memo: existingContract.cost?.memo || ''
        },
        documents: existingContract.documents || [], // documents 배열 로드
        stone_object: {
          tomb_type: existingContract.stone_object?.tomb_type || '',
          tomb_table: existingContract.stone_object?.tomb_table || '',
          memorial_stone: existingContract.stone_object?.memorial_stone || '',
          gravestone: existingContract.stone_object?.gravestone || '',
          vases: existingContract.stone_object?.vases?.length > 0 
            ? existingContract.stone_object.vases 
            : [{ size: '' }]
        }
      });
    }
  }, [existingContract, isEditMode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditMode) {
      updateContractMutation.mutate(formData);
    } else {
      createContractMutation.mutate(formData);
    }
  };

  if (isLoadingContract) {
    return <div className="loading">계약 정보를 불러오는 중...</div>;
  }

  return (
    <div>
      {/* 헤더 */}
      <div className="flex-between mb-6">
        <div className="flex" style={{ alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate(isEditMode ? `/contracts/${id}` : '/contracts')}
            className="btn btn-secondary"
            style={{ padding: '8px' }}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? '계약 수정' : '신규 계약 등록'}
          </h1>
        </div>

        <button
          type="submit"
          form="contract-form"
          className="btn btn-primary"
          disabled={createContractMutation.isLoading || updateContractMutation.isLoading}
        >
          <Save size={20} />
          {createContractMutation.isLoading || updateContractMutation.isLoading
            ? '저장 중...'
            : (isEditMode ? '수정 완료' : '계약 저장')
          }
        </button>
      </div>

      <form id="contract-form" onSubmit={handleSubmit}>
        {/* 계약 기본 정보 */}
        <div className="card form-section">
          <div className="form-section-title">
            <FileText size={20} className="text-blue-600" />
            <h2>계약 기본 정보</h2>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>계약일 *</label>
              <input
                type="date"
                value={formData.contract_date}
                onChange={(e) => handleInputChange(null, 'contract_date', e.target.value)}
                className="input input-text"
                required
              />
            </div>
            <div className="form-field">
              <label>계약 구분 *</label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange(null, 'type', e.target.value)}
                className="select select-type"
                required
              >
                <option value="신규">신규</option>
                <option value="이장">이장</option>
                <option value="연장">연장</option>
                <option value="해지">해지</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>계약 상태 *</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange(null, 'status', e.target.value)}
                className="select select-status"
                required
              >
                <option value="진행중">진행중</option>
                <option value="완료">완료</option>
                <option value="서류 대기">서류 대기</option>
                <option value="취소">취소</option>
              </select>
            </div>
            <div className="form-field">
              <label>&nbsp;</label>
              <div style={{ height: '38px' }}></div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field form-field-full">
              <label>비고</label>
              <textarea
                value={formData.memo}
                onChange={(e) => handleInputChange(null, 'memo', e.target.value)}
                className="input"
                rows="3"
                placeholder="추가 정보를 입력하세요"
              />
            </div>
          </div>
        </div>

        {/* 사망자 정보 */}
        <div className="card form-section">
          <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={20} className="text-green-600" />
              <h2>사망자 정보</h2>
            </div>
            <button
              type="button"
              onClick={addDead}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <Plus size={16} />
              사망자 추가
            </button>
          </div>


          {formData.dead.map((dead, index) => (
            <div key={index} className="person-section" style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              position: 'relative'
            }}>
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="text-sm font-medium text-gray-700">
                  사망자 {index + 1}
                </h3>
                {formData.dead.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDead(index)}
                    className="btn btn-danger"
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      minWidth: 'auto'
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>성명 *</label>
                  <input
                    type="text"
                    value={dead.name}
                    onChange={(e) => handleInputChange('dead', 'name', e.target.value, index)}
                    className="input input-text"
                    required
                  />
                </div>
                <div className="form-field">
                  <label>성별 *</label>
                  <select
                    value={dead.gender}
                    onChange={(e) => handleInputChange('dead', 'gender', e.target.value, index)}
                    className="select select-short"
                    required
                  >
                    <option value="">선택하세요</option>
                    <option value="남">남성</option>
                    <option value="여">여성</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>장례 구분1 *</label>
                  <select
                    value={dead.burial_method_1}
                    onChange={(e) => handleInputChange('dead', 'burial_method_1', e.target.value, index)}
                    className="select select-type"
                    required
                  >
                    <option value="">선택하세요</option>
                    <option value="입관">입관</option>
                    <option value="퇴관">퇴관</option>
                    <option value="봉안">봉안</option>
                    <option value="평장">평장</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>장례 구분2</label>
                  <select
                    value={dead.burial_method_2}
                    onChange={(e) => handleInputChange('dead', 'burial_method_2', e.target.value, index)}
                    className="select select-type"
                  >
                    <option value="">선택하세요</option>
                    <option value="단분">단분</option>
                    <option value="쌍분">쌍분</option>
                    <option value="합장">합장</option>
                    <option value="봉안단지">봉안단지</option>
                    <option value="매장단지">매장단지</option>
                    <option value="평장묘">평장묘</option>
                    <option value="가족평장묘">가족평장묘</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>묘지 유형 *</label>
                  <select
                    value={dead.tomb_type}
                    onChange={(e) => handleInputChange('dead', 'tomb_type', e.target.value, index)}
                    className="select select-type"
                    required
                  >
                    <option value="">선택하세요</option>
                    <option value="매장묘">매장묘</option>
                    <option value="평장묘">평장묘</option>
                    <option value="가족평장묘">가족평장묘</option>
                    <option value="납골묘">납골묘</option>
                    <option value="봉안묘">봉안묘</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>사망일</label>
                  <input
                    type="date"
                    value={dead.death_date}
                    onChange={(e) => handleInputChange('dead', 'death_date', e.target.value, index)}
                    className="input input-text"
                  />
                </div>
                <div className="form-field">
                  <label>설묘일</label>
                  <input
                    type="date"
                    value={dead.report_date}
                    onChange={(e) => handleInputChange('dead', 'report_date', e.target.value, index)}
                    className="input input-text"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 묘소 정보 */}
        <div className="card form-section">
          <div className="form-section-title">
            <MapPin size={20} className="text-purple-600" />
            <h2>묘소 정보</h2>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>회원 번호 *</label>
              <input
                type="text"
                value={formData.mausoleum.member_number}
                onChange={(e) => handleInputChange('mausoleum', 'member_number', e.target.value)}
                className="input input-text"
                required
              />
            </div>
            <div className="form-field">
              <label>단지</label>
              <input
                type="text"
                value={formData.mausoleum.complex}
                onChange={(e) => handleInputChange('mausoleum', 'complex', e.target.value)}
                className="input input-text"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>묘역</label>
              <input
                type="text"
                value={formData.mausoleum.area}
                onChange={(e) => handleInputChange('mausoleum', 'area', e.target.value)}
                className="input input-text"
                placeholder="예: 3평"
              />
            </div>
            <div className="form-field">
              <label>열</label>
              <input
                type="text"
                value={formData.mausoleum.row_number}
                onChange={(e) => handleInputChange('mausoleum', 'row_number', e.target.value)}
                className="input input-text"
                placeholder="예: A"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>번호</label>
              <input
                type="text"
                value={formData.mausoleum.number}
                onChange={(e) => handleInputChange('mausoleum', 'number', e.target.value)}
                className="input input-text"
                placeholder="예: 123"
              />
            </div>
            <div className="form-field">
              <label>크기 (평)</label>
              <NumberInput
                value={formData.mausoleum.area_size}
                onChange={(val) => handleInputChange('mausoleum', 'area_size', val)}
                allowDecimal={true}
                className="input input-text"
                placeholder="예: 3.5"
                min={0}
                step={0.1}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>기간</label>
              <input
                type="text"
                value={formData.mausoleum.period}
                onChange={(e) => handleInputChange('mausoleum', 'period', e.target.value)}
                className="input input-text"
                placeholder="예: 30년"
              />
            </div>
            <div className="form-field">
              <label>&nbsp;</label>
              <div style={{ height: '38px' }}></div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-field form-field-full">
              <label>기타</label>
              <textarea
                value={formData.mausoleum.etc}
                onChange={(e) => handleInputChange('mausoleum', 'etc', e.target.value)}
                className="input"
                rows="3"
                placeholder="묘소 관련 추가 정보를 입력하세요"
              />
            </div>
          </div>
        </div>

        {/* 연고자 정보 */}
        <div className="card form-section">
          <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={20} className="text-orange-600" />
              <h2>연고자 정보</h2>
            </div>
            <button
              type="button"
              onClick={addAcquaintance}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <Plus size={16} />
              연고자 추가
            </button>
          </div>


          {formData.acquaintances.map((acquaintance, index) => (
            <div key={index} className="person-section" style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              position: 'relative'
            }}>
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 className="text-sm font-medium text-gray-700">
                  연고자 {index + 1}
                </h3>
                {formData.acquaintances.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAcquaintance(index)}
                    className="btn btn-danger"
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      minWidth: 'auto'
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>성명 *</label>
                  <input
                    type="text"
                    value={acquaintance.name}
                    onChange={(e) => handleInputChange('acquaintances', 'name', e.target.value, index)}
                    className="input input-text"
                    required
                  />
                </div>
                <div className="form-field">
                  <label>관계 *</label>
                  <select
                    value={acquaintance.relationship}
                    onChange={(e) => handleInputChange('acquaintances', 'relationship', e.target.value, index)}
                    className="select select-type"
                    required
                  >
                    <option value="">선택하세요</option>
                    <option value="본인">본인</option>
                    <option value="배우자">배우자</option>
                    <option value="자녀">자녀</option>
                    <option value="부모">부모</option>
                    <option value="형제자매">형제자매</option>
                    <option value="기타">기타</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>연락처 *</label>
                  <input
                    type="tel"
                    value={acquaintance.phone}
                    onChange={(e) => handleInputChange('acquaintances', 'phone', e.target.value, index)}
                    className="input input-text"
                    required
                  />
                </div>
                <div className="form-field">
                  <label>&nbsp;</label>
                  <div style={{ height: '38px' }}></div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field form-field-full">
                  <label>주소</label>
                  <input
                    type="text"
                    value={acquaintance.address}
                    onChange={(e) => handleInputChange('acquaintances', 'address', e.target.value, index)}
                    className="input input-text"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 비용 정보 */}
        <div className="card form-section">
          <div className="form-section-title">
            <DollarSign size={20} className="text-green-600" />
            <h2>비용 정보</h2>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>사용료</label>
              <input
                type="text"
                value={formatNumber(formData.cost.usage_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'usage_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
            <div className="form-field">
              <label>관리비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.management_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'management_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>매장작업비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.burial_work_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'burial_work_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
            <div className="form-field">
              <label>설치비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.installation_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'installation_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>보수비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.repair_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'repair_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
            <div className="form-field">
              <label>제거작업비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.removal_work_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'removal_work_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>기타작업비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.other_work_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'other_work_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
            <div className="form-field">
              <label>석물가공비</label>
              <input
                type="text"
                value={formatNumber(formData.cost.stone_processing_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'stone_processing_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>대행수수료</label>
              <input
                type="text"
                value={formatNumber(formData.cost.agency_fee)}
                onChange={(e) => {
                  const numericValue = parseNumber(e.target.value);
                  handleInputChange('cost', 'agency_fee', numericValue);
                  setTimeout(calculateTotalCost, 100);
                }}
                className="input input-number"
                placeholder="0"
              />
            </div>
            <div className="form-field">
              <label>총 비용</label>
              <input
                type="text"
                value={formatNumber(formData.cost.total_cost)}
                className="input input-number"
                readOnly
                style={{ backgroundColor: '#f3f4f6' }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field" style={{ flex: '7' }}>
              <label>비용 관련 메모</label>
              <textarea
                value={formData.cost.memo}
                onChange={(e) => handleInputChange('cost', 'memo', e.target.value)}
                className="input input-text"
                rows="3"
                placeholder="비용 관련 추가 정보를 입력하세요"
              />
            </div>
            <div className="form-field" style={{ flex: '3' }}>
              <label>&nbsp;</label>
              <div style={{ height: '38px' }}></div>
            </div>
          </div>
        </div>

        {/* 서류 정보 */}
        <div className="card form-section">
          <div className="form-section-title">
            <FileCheck size={20} className="text-blue-600" />
            <h2>서류 정보</h2>
          </div>

          {/* 사망진단서 */}
          <div className="document-section mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">사망진단서</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.DEATH_CERTIFICATE, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="사망진단서 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.DEATH_CERTIFICATE)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.DEATH_CERTIFICATE, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

                          {/* 매장신고서 */}
          <div className="document-section mb-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">매장신고서</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.BURIAL_CERTIFICATE, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="매장신고서 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.BURIAL_CERTIFICATE)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.BURIAL_CERTIFICATE, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* 가족관계증명서 */}
          <div className="document-section mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">가족관계증명서</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.FAMILY_RELATION_CERTIFICATE, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="가족관계증명서 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.FAMILY_RELATION_CERTIFICATE)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.FAMILY_RELATION_CERTIFICATE, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* 제적등본 */}
          <div className="document-section mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">제적등본</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.FAMILY_REGISTER, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="제적등본 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.FAMILY_REGISTER)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.FAMILY_REGISTER, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* 화장증명서 */}
          <div className="document-section mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">화장증명서</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.CREMATION_CERTIFICATE, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="화장증명서 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.CREMATION_CERTIFICATE)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.CREMATION_CERTIFICATE, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* 개인정보동의서 */}
          <div className="document-section mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">개인정보동의서</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.PRIVACY_CONSENT, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="개인정보동의서 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.PRIVACY_CONSENT)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.PRIVACY_CONSENT, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* 기타 서류 */}
          <div className="document-section mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">기타 서류</h3>
            <div className="flex gap-4">
              {/* 파일 업로드 영역 - 왼쪽 고정 */}
              <div className="flex-shrink-0" style={{ width: '200px' }}>
                <FileUpload
                  label=""
                  value=""
                  onChange={(value, fileName) => handleDocumentChange(DOCUMENT_TYPES.OTHER, value, fileName)}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.rtf,.zip,.rar"
                  placeholder="기타 서류 파일을 업로드하세요"
                />
              </div>

              {/* 업로드된 파일들 - 오른쪽에 나열 */}
              <div className="flex-1 flex flex-wrap gap-3">
                {formData.documents
                  .filter(doc => doc.document_type === DOCUMENT_TYPES.OTHER)
                  .map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md min-w-0 flex-1" style={{ maxWidth: '300px' }}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <File size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-sm text-green-800 font-medium truncate">
                          {doc.file_name || getFileNameFromUrl(doc.file_link)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDocument(DOCUMENT_TYPES.OTHER, doc.file_link)}
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#fef2f2',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                          marginLeft: '8px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#fecaca';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fef2f2';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title="파일 삭제"
                      >
                        <X size={12} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* 석물 정보 */}
        <div className="card form-section">
          <div className="form-section-title">
            <Building size={20} className="text-purple-600" />
            <h2>석물 정보</h2>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>묘지 종류</label>
              <select
                value={formData.stone_object.tomb_type}
                onChange={(e) => handleInputChange('stone_object', 'tomb_type', e.target.value)}
                className="select select-type"
              >
                <option value="">선택하세요</option>
                <option value="매장묘">매장묘</option>
                <option value="평장묘">평장묘</option>
                <option value="가족평장묘">가족평장묘</option>
                <option value="납골묘">납골묘</option>
                <option value="봉안묘">봉안묘</option>
              </select>
            </div>
            <div className="form-field">
              <label>묘테</label>
              <input
                type="text"
                value={formData.stone_object.tomb_table}
                onChange={(e) => handleInputChange('stone_object', 'tomb_table', e.target.value)}
                className="input input-text"
                placeholder="묘테 종류"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-field">
              <label>상석</label>
              <input
                type="text"
                value={formData.stone_object.memorial_stone}
                onChange={(e) => handleInputChange('stone_object', 'memorial_stone', e.target.value)}
                className="input input-text"
                placeholder="상석 종류"
              />
            </div>
            <div className="form-field">
              <label>비석</label>
              <input
                type="text"
                value={formData.stone_object.gravestone}
                onChange={(e) => handleInputChange('stone_object', 'gravestone', e.target.value)}
                className="input input-text"
                placeholder="비석 종류"
              />
            </div>
          </div>

          <div className="form-section-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 className="text-lg font-medium text-gray-900">화병 정보</h3>
            </div>
            <button
              type="button"
              onClick={addVase}
              className="btn btn-secondary"
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              <Plus size={16} />
              화병 추가
            </button>
          </div>

          {formData.stone_object.vases.map((vase, index) => (
            <div key={index} className="person-section" style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px',
              position: 'relative'
            }}>
              {formData.stone_object.vases.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeVase(index)}
                  className="btn btn-danger"
                  style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    padding: '4px 8px',
                    fontSize: '12px',
                    minWidth: 'auto'
                  }}
                >
                  <X size={14} />
                </button>
              )}

              <div style={{ marginBottom: '12px' }}>
                <h3 className="text-sm font-medium text-gray-700">
                  화병 {index + 1}
                </h3>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>사이즈 *</label>
                  <select
                    value={vase.size}
                    onChange={(e) => handleInputChange('vases', 'size', e.target.value, index)}
                    className="select select-type"
                    required
                  >
                    <option value="">선택하세요</option>
                    <option value="소형">소형</option>
                    <option value="중형">중형</option>
                    <option value="대형">대형</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>&nbsp;</label>
                  <div style={{ height: '38px' }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </form>
    </div>
  );
};

export default ContractForm; 